<template>
    <main id="LmUsersNewOrEdit" class="wrapper-content-with-toolbar">
      <LmComponentLoading v-if="isLoading" fixed />
      <b-row>
        <b-col>
          <div class="content">
          <h1 v-if="!isOnEditMode">Nuevo Usuario</h1>
          <h1 v-if="isOnEditMode">Editar Usuario</h1>
          <b-row>
            <LmTextInput
              class="col-6"
              @enter="onSubmit"
              :v="$v.form.firstname"
              v-model="form.firstname"
              label="Nombre"
            />
            <LmTextInput
              class="col-6"
              @enter="onSubmit"
              :v="$v.form.lastname"
              v-model="form.lastname"
              label="Apellido"
            />
            <LmEmailInput
              class="col-6"
              @enter="onSubmit"
              :v="$v.form.email"
              v-model="form.email"
              label="Email"
            />
            <LmTreeSelect
              class="col-6"
              :v="$v.form.role"
              label="Rol"
              pathToGetOptions="/customer/roles"
              v-model="form.role"
            />
            <LmTreeSelect
              class="col-6"
              label="Área"
              pathToGetOptions="/customer/areas"
              :clearable="true"
              v-model="form.area"
            />
            <LmTreeSelect
              class="col-6"
              label="Subárea"
              pathToGetOptions="/customer/subareas"
              childrenOf="area"
              :clearable="true"
              v-model="form.subarea"
            />
            <LmTreeSelect
              ref="userType"
              class="col-6"
              label="Tipo de usuario"
              pathToGetOptions="/users/user_types"
              v-model="form.userType"
            />
          </b-row>

          <!-- Action Buttons -->
          <div v-if="!isLoading" class="buttons">
            <LmButton
              :value="$t('cancel')"
              type="button"
              buttonClass="btn-secondary"
              @click="onCancel()"
            />
            <LmButton
              :value="isOnEditMode ? $t('save') : $t('invite')"
              type="button"
              buttonClass="btn-primary"
              @click="onSubmit()"
            />
            <LmButton
              v-if="isOnEditMode && !form.deleted"
              :value="$t('delete')"
              action="delete"
              buttonClass="btn-danger"
              confirmationTitle="Vas a eliminar el usuario"
              @click="onDelete()"
            />
            <LmButton
              v-if="isOnEditMode && form.deleted"
              :value="$t('Eliminar definitivamente')"
              action="delete"
              buttonClass="btn-danger"
              confirmationTitle="Vas a eliminar el usuario definitivamente"
              confirmationMessage="La action no puede ser revertida. ¿Está seguro?"
              @click="onDeleteDefinitive()"
            />
            <LmButton
              v-if="isOnEditMode && form.deleted"
              value="Reactivar"
              buttonClass="btn-success"
              @click="onReactivate()"
            />
            <LmButton
              v-if="isOnEditMode && !form.invitationAccepted"
              :value="$t('resend_activation_email')"
              action="delete"
              buttonClass="btn-success"
              confirmationTitle="Reenviar invitación"
              confirmationMessage="Vamos a enviarle nuevamente el email de invitación al usuario.  <br><br>¿Está seguro?"
              @click="onResendInvitation()"
            />
          </div>
        </div>
        </b-col>
        <!-- Toolbar -->
        <LmToolbar
          ref="toolbar"
          :isOnEditMode="isOnEditMode"
          :items="toolbarItems"
          :config="toolbarConfig"
        />
      </b-row>
    </main>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmForm from '@/components/form/LmForm'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmToolbar from '@/components/lmToolBar'

// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'
import { required, email } from 'vuelidate/lib/validators'

export default {
  provide () {
    return {
      context: this.form
    }
  },
  mixins: [lmToasts],
  components: {
    LmForm,
    LmButton,
    LmTextInput,
    LmEmailInput,
    LmTreeSelect,
    LmToolbar
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    pathToRedirectOnCancel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      deleted: false,
      toolbarMode: null,
      form: {
        area: null,
        subarea: null,
        role: null,
        firstname: '',
        lastname: '',
        email: '',
        deleted: false,
        userType: '',
        invitationAccepted: false
      },
      isLoading: true
    }
  },
  validations: {
    form: {
      firstname: {
        required
      },
      lastname: {
        required
      },
      email: {
        required,
        email
      },
      role: {
        required
      }
    }
  },
  computed: {
    pathToGetOptionsForSubAreas () {
      if (this.form.area) {
        return `/customer/subareas/${this.form.area.id}`
      }
      return null
    },
    toolbarItems () {
      let items = []
      if (this.isOnEditMode) {
        items = [
          'logMovements',
          'auditMovements'
        ]
      }
      return items
    },
    toolbarConfig () {
      return {
        auditMovements: {
          serviceName: 'users'
        },
        logMovements: {}
      }
    }
  },
  methods: {
    async onSubmit () {
      try {
        this.isLoading = true
        // Convierte userType de Objeto a String esperado
        const f = { ...this.form }
        f.userType = f.userType.value
        // Validaciones
        this.$v.$touch()
        await this.validateForm(this.form)
        if (this.$v.$invalid) {
          return this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        }
        if (!this.isOnEditMode) {
          // Nuevo
          await commonsApi.axiosPostWrapper('/users/', f)
          this.lmToastSuccessMessage(`Se creó el usuario y recibirá la invitación por email.`, 'Se guardó exitosamente')
        } else {
          // Editar
          await commonsApi.axiosPutWrapper(`/users/update/${this.$route?.params?.id}`, f)
          this.lmToastSuccessMessage(`Se editó el usuario correctamente.`, 'Se guardó exitosamente')
        }

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onCancel () {
      this.$router.push(this.pathToRedirectOnCancel)
    },
    validateForm (validation) {
      // Valido el email con https://emailregex.com/
      const emailregex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      if (!emailregex.test(validation.email)) {
        this.lmToastDangerMessage(this.$t('El email ingresado no es válido'), 'Error')
        throw new Error('validation error')
      }
    },
    async onDelete () {
      try {
        this.isLoading = true

        await commonsApi.axiosPutWrapper(`/users/delete/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`Se eliminó el usuario correctamente.`, 'Se eliminó correctamente')

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onDeleteDefinitive () {
      try {
        this.isLoading = true

        await commonsApi.axiosPutWrapper(`/users/remove_definitive/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`Se eliminó el usuario definitivamente.`, 'Se eliminó definitivamente')

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onReactivate () {
      try {
        this.isLoading = true

        await commonsApi.axiosPutWrapper(`/users/reactivate/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`Se reactivó el usuario correctamente.`, 'Se reactivó correctamente')

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onResendInvitation () {
      try {
        this.isLoading = true

        await commonsApi.axiosPutWrapper(`/users/sendinvitation/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`Volvimos a enviarle la invitación al usuario.`, 'Se reactivó correctamente')

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    }
  },
  async mounted () {
    if (this.$route?.params?.id) {
      try {
        const user = await commonsApi.axiosGetWrapper(`/users/${this.$route?.params?.id}`)
        for (const prop in user.data) {
          if (this.form.hasOwnProperty(prop)) {
            // userType está guardado como string, necesito convertirlo a objeto para que el treeselect quede seleccionado
            if (prop === 'userType') {
              // filtro las opciones del treeselect que name coincida con el valor guardado
              const options = [...this.$refs.userType.optionsFromPath]
              const value = options.filter(el => el.value === user.data[prop])
              if (value) {
                const capitalize = value[0].name
                value[0].name = capitalize.charAt(0).toUpperCase() + capitalize.slice(1)
                this.form.userType = value[0]
              }
            }
            if (prop !== 'userType') {
              this.form[prop] = user.data[prop]
            }
          }
        }
      } catch (error) {
        console.log(error)
      }
    }
    this.isLoading = false
    // Creamos un watcher para limpiar la subarea cuando se cambia el area
    // Debe estar despues de que se cargan los values en modo edición
    // this.$nextTick(() => {
    //   this.$watch('form.area', _ => {
    //     this.form.subarea = null
    //   })
    // })
  }
}

</script>

<style>
.button {
  margin-top: 35px;
}
.content{
  margin-right: 14px;
}
</style>
